'use strict';

import { Color, LinearSRGBColorSpace, MathUtils, Mesh, ShaderMaterial, TextureLoader } from 'three';
import vertex from './vertex.glsl';
import fragment from './fragment.glsl';
import { projectMode } from '../../../../settings';
import { Weather } from '../../../components/weather/Weather';
import {Cloudflare} from "../../../api/Cloudflare";


export const CLOUDS = {
    0: {
        cover: 0.0,
        alpha: 0.0
    },
    10: {
        cover: 0.1,
        alpha: 4.0
    },
    20: {
        cover: 0.2,
        alpha: 4.0
    },
    30: {
        cover: 0.3,
        alpha: 3.0
    },
    40: {
        cover: 0.4,
        alpha: 2.5
    },
    50: {
        cover: 0.5,
        alpha: 1.5
    },
    60: {
        cover: 0.6,
        alpha: 1.3
    },
    70: {
        cover: 0.7,
        alpha: 1.0
    },
    80: {
        cover: 0.8,
        alpha: 0.9
    },
    90: {
        cover: 0.9,
        alpha: 0.8
    },
    100: {
        cover: 1.0,
        alpha: 0.6
    }
};


export class Sky {
    static #active = config.features.liteMode?.settings.sky?.active;
    static #skySpeed = config.features.liteMode?.settings.sky?.skySpeed || 0.002;
    static #direction = config.features.liteMode?.settings.sky?.direction || 1.0;
    static #scale = config.features.liteMode?.settings.sky?.scale || 1.0;
    static #dark = config.features.liteMode?.settings.sky?.dark || 0.0;
    static #light = config.features.liteMode?.settings.sky?.light || 0.2;
    static #cover = config.features.liteMode?.settings.sky?.cover || 0.01;
    static #alpha = config.features.liteMode?.settings.sky?.alpha || 4.0;
    static #tint = config.features.liteMode?.settings.sky?.tint || 0.5;
    static #horizon = config.features.liteMode?.settings.sky?.horizon || 2.0;
    static #rainbowSettings = config.features.liteMode?.settings.sky?.rainbow || {scale: 1.0, position: 0.0};
    static #gradient = config.features.liteMode?.settings.sky?.gradient || {
        mixingLine: 0.25,
        colors: {
            day: {
                bottom: '#ffffff',
                top: '#0078cd'
            },
            night: {
                bottom: '#12122e',
                top: '#141675'
            }
        }
    };
    static #ready = false;
    static #weatherPreset = null;
    #url = null;
    #loader = null;
    #mesh = null;
    static #material = null;
    #rainbow = null;

    constructor (url,loader) {
        this.#url = `${url}${config.features.liteMode.settings.sky?.path}?v=${Cloudflare.version}` || '';
        this.#loader = loader;

        return this;
    }

    create(){
        if (!Sky.#active) return new Promise(resolve => resolve('disabled'));

        return new Promise(resolve => {

            this.#loader.load(this.#url, model => {
                model.traverse(el => {
                    if (el.isMesh) {
                        const geometry = el.geometry.clone();
                        Sky.#material = new ShaderMaterial({
                            vertexShader: vertex,
                            fragmentShader: fragment,
                            uniforms: {
                                dayBottomColor: {
                                    value: new Color().setStyle(Sky.#gradient.colors.day.bottom,LinearSRGBColorSpace)
                                },
                                dayTopColor: {
                                    value: new Color().setStyle(Sky.#gradient.colors.day.top,LinearSRGBColorSpace)
                                },
                                nightBottomColor: {
                                    value: new Color().setStyle(Sky.#gradient.colors.night.bottom,LinearSRGBColorSpace)
                                },
                                nightTopColor: {
                                    value: new Color().setStyle(Sky.#gradient.colors.night.top,LinearSRGBColorSpace)
                                },
                                mixingLine: {
                                    value: Sky.#gradient.mixingLine
                                },
                                // skyTexture: {
                                //     value: this.#dayTexture
                                // },
                                // nightSkyTexture: {
                                //     value: this.#nightTexture
                                // },
                                uHorizon: {
                                    value: Sky.#horizon
                                },
                                uSkySpeed: {
                                    value: Sky.#skySpeed
                                },
                                uDirection: {
                                    value: Sky.#direction
                                },
                                uScale: {
                                    value: Sky.#scale
                                },
                                uDark: {
                                    value: Sky.#dark
                                },
                                uLight: {
                                    value: Sky.#light
                                },
                                uCover: {
                                    value: Sky.#cover
                                },
                                uAlpha: {
                                    value: Sky.#alpha
                                },
                                uTint: {
                                    value: Sky.#tint
                                },
                                rainbowTexture: {
                                    value: null
                                },
                                uRainbowActive: {
                                    value: false
                                },
                                uRainbowY: {
                                    value: Sky.#rainbowSettings.position
                                },
                                uRainbowScale: {
                                    value: Sky.#rainbowSettings.scale
                                },
                                uTime: {
                                    value: 0.0
                                },
                                uTimesOfDay: {
                                    value: 0.0
                                }
                            },
                            depthTest: false,
                            depthWrite: false,
                            transparent: true,
                            opacity: 1
                        });

                        this.#mesh = new Mesh(geometry,Sky.#material);
                        this.#mesh.name = el.name;
                        this.#mesh.scale.copy(el.scale);
                        this.#mesh.position.set(0,0,-0.2);
                        Sky.#ready = true;
                        this.#debug();
                        resolve(this.#mesh);
                    }
                });
            });

        });

    }

    updateTime(time) {
        if (Sky.#ready) {
            Sky.#material.uniforms.uTime.value += time;
        }
    }

    updateTimesOfDay(time){
        if (Sky.#ready) {
            Sky.#material.uniforms.uTimesOfDay.value = time;
        }
    }

    switcher(state) {
        if (Sky.#ready) {
            switch (state) {
                case 'show':
                    this.#mesh.visible = true;
                    break;
                case 'hide':
                    this.#mesh.visible = false;
                    break;

            }
        }
    }

    async rainbowToggle(action) {
        if (!Sky.#ready) return;

        switch (action) {
            case 'enable':
                if (!this.#rainbow) {
                    const textureLoader = new TextureLoader();
                    this.#rainbow = await textureLoader.loadAsync('./assets/textures/rainbow/rainbow-lite.png');
                }

                Sky.#material.uniforms.rainbowTexture.value = this.#rainbow;
                Sky.#material.uniforms.uRainbowActive.value = true;

                break;
            case 'disable':
                Sky.#material.uniforms.rainbowTexture.value = null;
                Sky.#material.uniforms.uRainbowActive.value = false;
                break;
        }
    }

    #getCloudsPreset() {
        const weatherData = Weather.getWeatherData();
        const data = {};

        if (weatherData.hasOwnProperty('clouds')) {
            const cloudsDensity = weatherData.clouds.all;
            data.clouds = {};

            if (cloudsDensity === 0){
                data.clouds.preset =  0;
            } else if (cloudsDensity > 0 && cloudsDensity <= 10) {
                data.clouds.preset = 10;
            } else if (cloudsDensity > 10 && cloudsDensity <= 20) {
                data.clouds.preset = 20;
            } else if (cloudsDensity > 20 && cloudsDensity <= 30) {
                data.clouds.preset = 30;
            } else if (cloudsDensity > 30 && cloudsDensity <= 40) {
                data.clouds.preset = 40;
            } else if (cloudsDensity > 40 && cloudsDensity <= 50) {
                data.clouds.preset = 50;
            } else if (cloudsDensity > 50 && cloudsDensity <= 60) {
                data.clouds.preset = 60;
            } else if (cloudsDensity > 60 && cloudsDensity <= 70) {
                data.clouds.preset = 70;
            } else if (cloudsDensity > 70 && cloudsDensity <= 80) {
                data.clouds.preset = 80;
            } else if (cloudsDensity > 80 && cloudsDensity < 100) {
                data.clouds.preset = 90;
            } else  {
                data.clouds.preset = 100;
            }
        } else {
            data.clouds.preset = 0;
        }

        data.wind = weatherData.wind;

        return data;
    }

    cloudsToggle(action){
        if (!Sky.#ready) return;

        switch (action) {
            case 'enable':
                // Sky.#material.uniforms.uCover.value = config.features.liteMode.settings.sky.cover + 1.5;

                if (Weather.checkedCurrentWeather()) {
                    const preset = this.#getCloudsPreset();
                    Sky.setData(preset,true);
                } else {
                    Sky.setData({clouds: {preset: 90 } });
                }

                break;
            case 'disable':

                if (Weather.checkedCurrentWeather()) {
                    if (Sky.#weatherPreset) {
                        Sky.setData(Sky.#weatherPreset);
                    } else {
                        Sky.resetToDefault();
                    }
                } else {
                    // Sky.resetToDefault();
                    Sky.setData({clouds: {preset: 0 } });
                }

                // Sky.#material.uniforms.uCover.value = config.features.liteMode.settings.sky.cover;

                break;
        }
    }

    static setData(data,save) {
        if (!Sky.#active || !Sky.#ready) return;

        if (save) {
            Sky.#weatherPreset = data;
        }

        const usePreset = CLOUDS[data.clouds.preset];

        if (data.hasOwnProperty('wind')){
            const skyDirection = data.wind.deg;
            const skySpeed = data.wind.speed;
            Sky.#material.uniforms.uSkySpeed.value = skySpeed / 1000;
            Sky.#material.uniforms.uDirection.value = MathUtils.degToRad(skyDirection);
        }


        Sky.#material.uniforms.uCover.value = usePreset.cover;
        Sky.#material.uniforms.uAlpha.value = usePreset.alpha;

    }

    static resetToDefault() {
        if (!Sky.#active || !Sky.#ready) return;

        Sky.#material.uniforms.uCover.value = Sky.#cover;
        Sky.#material.uniforms.uAlpha.value = Sky.#alpha;
        Sky.#material.uniforms.uSkySpeed.value = Sky.#skySpeed;
        Sky.#material.uniforms.uDirection.value = Sky.#direction;
    }

    #debug() {
        if (!projectMode.LiteMode.debug) return;

        const params = {
            scale: Sky.#scale,
            dark: Sky.#dark,
            light: Sky.#light,
            cover: Sky.#cover,
            alpha: Sky.#alpha,
            tint: Sky.#tint,
            horizon: Sky.#horizon,
            direction: Sky.#direction,
            speed: Sky.#skySpeed,
            mixingLine: Sky.#gradient.mixingLine,
            dayBottomColor: Sky.#gradient.colors.day.bottom,
            dayTopColor: Sky.#gradient.colors.day.top,
            nightBottomColor: Sky.#gradient.colors.night.bottom,
            nightTopColor: Sky.#gradient.colors.night.top,
        };


        const folder = projectMode.LiteMode.debugFolder.addFolder('Clouds');
        const skyFolder = projectMode.LiteMode.debugFolder.addFolder('Sky');

        skyFolder.add(params,'mixingLine',-1.0,1.0,0.001).onChange(value => {
            Sky.#material.uniforms.mixingLine.value = value;
        });

        skyFolder.addColor(params,'dayBottomColor').onChange(value => {
            Sky.#material.uniforms.dayBottomColor.value.set(value);
        });

        skyFolder.addColor(params,'dayTopColor').onChange(value => {
            Sky.#material.uniforms.dayTopColor.value.set(value);
        });

        skyFolder.addColor(params,'nightBottomColor').onChange(value => {
            Sky.#material.uniforms.nightBottomColor.value.set(value);
        });

        skyFolder.addColor(params,'nightTopColor').onChange(value => {
            Sky.#material.uniforms.nightTopColor.value.set(value);
        });

        folder.add(params,'scale', 0.0, 10.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uScale.value = value;
        });

        folder.add(params,'dark', 0.0, 5.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uDark.value = value;
        });

        folder.add(params,'light', 0.0, 5.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uLight.value = value;
        });

        folder.add(params,'cover', 0, 5.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uCover.value = value;
        });

        folder.add(params,'alpha', 0.0, 10.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uAlpha.value = value;
        });

        folder.add(params,'tint', -2.0, 2.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uTint.value = value;
        });

        folder.add(params,'horizon', -2.0, 2.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uHorizon.value = value;
        });

        folder.add(params,'direction', -Math.PI, Math.PI, 0.001).onChange(value => {
            Sky.#material.uniforms.uDirection.value = value;
        });

        folder.add(params,'speed', 0.0, 1.0, 0.001).onChange(value => {
            Sky.#material.uniforms.uSkySpeed.value = value;
        });
    }
}